import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import styles from "../Components/Products/Product.module.css";
import ProductSummary from "../Components/Products/ProductSummary";
import ProductDetails from "../Components/Products/ProductDetails";
import ProductSpecifications from "../Components/Products/ProductSpecifications";

import type { IProductData } from "@model/Products.model";
import { ProductsService } from "../../Logic/ProductsService";
import { CartService } from "../../Logic/CartService";
import useImageUploadRules from "../../Logic/Functions/Hooks/useImageUploadRules";
import { UserAuthenticationStoreImplementation } from "Data/UserAuthentication/UserAuthenticationStoreImplementation";
import { UserCartStoreImplementation } from "Data/Cart/CartStoreImplementation";
import { AccountModeModalStoreImplementation } from "Data/AccountModeModal/AccountModeModalStoreImplementation";
import ProductNotFoundState from "../Components/Products/ProductNotFoundState";
import { useForceLogoutAndRedirect } from "../../Logic/Functions/Hooks/useForceLogoutAndRedirect";
import ProductTechnicalSheet from "../Components/Products/ProductTechnicalSheet";

const SingleProduct: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();

  const [product, setProduct] = useState<IProductData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const { openAccountModeModal } = AccountModeModalStoreImplementation();

  const [quantity, setQuantity] = useState<number>(1);
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState<boolean>(false);

  const { authenticationState } = UserAuthenticationStoreImplementation();
  const authToken = authenticationState?.token?.trim() ?? "";
  const isLoggedIn = !!authToken;

  const onAuthError = useForceLogoutAndRedirect();
  const cartService = useMemo(() => new CartService(), []);
  const { itemsICart, addItemToCart } = UserCartStoreImplementation(
    "USD",
    cartService.mapCartStateToICartItems,
    cartService.computeCartSubtotalFromICartItems
  );

  const { buildImageSrc } = useImageUploadRules({
    maxFiles: 1,
    maxSizeMB: 5,
    requireAtLeastOne: false,
    enforceTotalSize: false,
  });

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;

    const productService = new ProductsService();
    productService
      .handleGetProductBySlug({
        slug,
        setLoading: (v) => !cancelled && setLoading(v),
        setApiError: (m) => !cancelled && setApiError(m),
        setProduct: (p) => !cancelled && setProduct(p),
      })
      .catch((e: unknown) => {
        const msg = e instanceof Error ? e.message : String(e);
        if (!cancelled) setApiError(msg);
      });

    return () => {
      cancelled = true;
    };
  }, [slug]);

  const currentInCart = useMemo(
    () =>
      product
        ? Number(itemsICart.find((it) => it.id === product.id)?.qty ?? 0)
        : 0,
    [itemsICart, product]
  );

  const stockNum = useMemo<number>(
    () =>
      typeof product?.stock === "number" && Number.isFinite(product.stock)
        ? product.stock
        : 0,
    [product?.stock]
  );

  const perUserCapNumber = useMemo(() => {
    const v = product?.maxPerUser;
    return typeof v === "number" && Number.isFinite(v) && v > 0
      ? v
      : Number.POSITIVE_INFINITY;
  }, [product?.maxPerUser]);

  const perUserCap = Number.isFinite(perUserCapNumber)
    ? (perUserCapNumber as number)
    : undefined;

  const stockRemaining = Math.max(0, stockNum - currentInCart);
  const limitRemaining = Math.max(0, perUserCapNumber - currentInCart);
  const remaining = Math.max(0, Math.min(stockRemaining, limitRemaining));

  const noStockLeft = stockRemaining <= 0;
  const userLimitHit =
    !noStockLeft && Number.isFinite(perUserCapNumber) && limitRemaining <= 0;
  const qtyTooHigh = quantity > remaining;

  const message =
    remaining === 0
      ? noStockLeft
        ? "0 left item is out of stock"
        : userLimitHit
          ? "Limit reached"
          : ""
      : qtyTooHigh
        ? `Only ${remaining} left`
        : "";

  useEffect(() => {
    setError(message);
  }, [message]);

  const addDisabled = remaining === 0 || !!message || working;

  const handleAddClick = useCallback(
    async (qty: number) => {
      if (remaining === 0 || working || !product) return;

      await cartService.handleAddItemToCart({
        isLoggedIn,
        token: authToken,
        productId: product.id,
        requestQty: qty,
        stockCap: stockNum > 0 ? stockNum : undefined,
        perUserCap: perUserCap,
        remaining,
        stockRemaining,
        limitRemaining,
        onAuthError,
        onGuestAdd: (qtyToAdd) => {
          if (!product) return;
          const displayImage =
            productImages[0] ??
            buildImageSrc(product.imageUrl ?? null) ??
            "";

          addItemToCart({
            id: product.id,
            name: product.name,
            price: product.price ?? 0,
            imageUrl: displayImage,
            shortDescription: product.shortDescription ?? "",
            stock: stockNum,
            maxPerUser: perUserCap,
            quantity: qtyToAdd,
          });
        },
        setLoading: setWorking,
        setApiError: (msg) => setError(msg),
        setSuccessMsg: (msg) => setError(msg ?? ""),
      });
      if (!isLoggedIn) {
        try {
          openAccountModeModal();
        } finally {
          setLoading(false);
        }
        return;
      }
    },
    [
      isLoggedIn,
      authToken,
      product,
      stockNum,
      perUserCap,
      remaining,
      stockRemaining,
      limitRemaining,
      addItemToCart,
      buildImageSrc,
      cartService,
      working,
      openAccountModeModal,
    ]
  );

  const renderNotFound = () => (
    <main
      className={styles.notsingleProductPageSP}
      data-testid="single-product-page"
    >
      <ProductNotFoundState />
    </main>
  );


  if (!slug) return null;

  if (loading) {
    return (
      <main
        className={styles.singleProductPageSP}
        data-testid="single-product-page"
      >
        <div
          className={styles.pagePlaceholderSP}
          data-testid="loading-placeholder"
        >
          Loading…
        </div>
      </main>
    );
  }

  if (apiError) {
    const normalized = apiError.toLowerCase();
    const isNotFoundError =
      normalized.includes("not found") || normalized.includes("404");

    if (isNotFoundError) {
      return renderNotFound();
    }

    return (
      <main
        className={styles.singleProductPageSP}
        data-testid="single-product-page"
      >
        <div
          className={styles.apiErrorSP}
          role="alert"
          data-testid="api-error"
        >
          {apiError}
        </div>
      </main>
    );
  }

  if (!product) {
    return renderNotFound();
  }

  const productImages = (product?.images ?? [])
    .map((img) => buildImageSrc(img?.url ?? null))
    .filter(Boolean) as string[];

  return (
    <main
      className={styles.singleProductPageSP}
      data-testid="single-product-page"
    >
      <div data-testid="single-product-summary">
        <ProductSummary
          title={product.name}
          description={product.shortDescription ?? ""}
          images={productImages}
          price={product.price}
          currency={product.currency ?? "USD"}
          quantity={quantity}
          stock={product.stock}
          maxPerUser={perUserCap}
          onAddToCart={handleAddClick}
          addDisabled={addDisabled}
          helperText={message || error || ""}
          onQuantityChangeExternal={setQuantity}
        />
      </div>

      <div data-testid="single-product-details">
        <ProductDetails details={product.longDescription ?? ""} />
      </div>

      <div data-testid="single-product-specs">
        <ProductSpecifications specs={product.specs ?? []} />
      </div>

      {product?.files && (
        <div data-testid="single-product-techsheet">
          <ProductTechnicalSheet files={product.files} />
        </div>
      )}


    </main>
  );
};

export default SingleProduct;
